<template>
  <div class="card h-100">
    <div class="card-body">
      <!-- Изображение -->
      <div class="text-center mb-2 thumb-container">
        <div v-if="material.duration" class="duration">
          <span><i class="bi bi-clock"></i> {{ material.duration }}</span>
        </div>
        <img
          v-if="material.smallpicture"
          :src="material.smallpicture"
          alt=""
          class="img-thumbnail img-cover"
        />
        <!-- Placeholder для материалов без изображения -->
        <div
          v-else
          class="img-thumbnail img-cover placeholder-image d-flex align-items-center justify-content-center"
        >
          <i class="bi bi-music-note-beamed fs-1 text-muted"></i>
        </div>
      </div>
      
      <!-- DEBUG: {{isPreview}} | {{isReallyPreviewMode}} -->
      
      <!-- Контент для preview режима (в боте) -->
      <div class="bottom-part" v-if="isReallyPreviewMode">
        <h5 class="card-title">{{ material.caption }}</h5>
        <button 
          class="btn btn-primary w-100 w-sm-auto mt-3" 
          @click="handleMaterialClick"
        >
          Открыть
        </button>
      </div>
      
      <!-- Контент для обычного режима (веб) -->
      <div class="bottom-part" v-if="!isReallyPreviewMode">
        <h5 class="card-title">
          <router-link 
            :to="{ name: 'Article', params: { id: material.hash } }"
            v-if="!disableLinks"
          >
            {{ material.caption }}
          </router-link>
          <span v-else>{{ material.caption }}</span>
        </h5>
        
        <!-- Дата добавления -->
        <div v-if="material.timeadd_original">
          <span class="small text-muted">{{ material.timeadd_original }}</span>
        </div>
        
        <!-- Статус материала -->
        <div 
          class="small" 
          :class="material.status_color ? 'text-' + material.status_color : 'text-info'" 
          v-if="material.status_caption"
        >
          <i :class="(material.status_icon) ? 'bi ' + material.status_icon : ''"></i> 
          {{ material.status_caption }}
        </div>
        
        <!-- Статус доступа -->
        <div v-if="material.open_status === 0">
          <span class="badge bg-danger">🔒 Закрыто</span>&nbsp;
          <span v-if="material.open_by_date" class="text-muted small">
            Доступ будет открыт {{ material.open_by_date }}
          </span>
          <span v-if="material.open_by_test_score" class="text-muted small">
            Нужна оценка за предыдущий тест: {{ material.open_by_test_score }} баллов
          </span>
        </div>
        <div v-else-if="material.open_status === 1">
          <span class="badge bg-success">Открыто</span>&nbsp;
        </div>
        <div v-else-if="material.open_status === 2">
          <span class="badge bg-success">Открыто</span>&nbsp;
          <span v-if="material.open_by_test_score" class="text-muted small">
            Оценка за предыдущий тест: {{ material.open_status_test_score }}
          </span>
        </div>
        
        <!-- Дополнительная информация для фильтрации -->
        <div v-if="showFilterInfo" class="mt-2">
          <!-- Локация -->
          <div v-if="material.location" class="small text-muted">
            <i class="bi bi-geo-alt"></i> {{ material.location }}
          </div>
          <!-- Теги -->
          <div v-if="material.tags && material.tags.length" class="mt-1">
            <span 
              v-for="tag in material.tags" 
              :key="tag.id"
              class="badge me-1 tag-badge"
              :style="{ backgroundColor: tag.color || '#6c757d' }"
            >
              {{ tag.name }}
            </span>
          </div>
          <!-- Уровень подготовки -->
          <div v-if="material.level_caption" class="small text-muted mt-1">
            <i class="bi bi-bar-chart"></i> {{ material.level_caption }}
          </div>
        </div>
        
        <!-- Кнопка действия -->
        <div v-if="showAction" class="mt-3">
          <button 
            class="btn btn-primary w-100 w-sm-auto" 
            @click="handleMaterialClick"
          >
            {{ actionText || 'Open' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'MaterialCard',
  props: {
    material: {
      type: Object,
      required: true
    },
    isPreview: {
      type: [Boolean, String],
      default: false
    },
    disableLinks: {
      type: Boolean,
      default: false
    },
    showFilterInfo: {
      type: Boolean,
      default: false
    },
    showAction: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: null
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    
    // Computed свойство для строгой проверки preview режима
    const isReallyPreviewMode = computed(() => {

      // Теперь должно быть четко true или false
      const result = Boolean(props.isPreview);

      return result;
    });
    
    const handleMaterialClick = () => {
      if (isReallyPreviewMode.value) {
        // В режиме preview отправляем команду боту
        store.dispatch('tgconnector/sendCommandToBot', {
          params: { 
            material_hash: props.material.hash, 
            command: 'show_channel_material' 
          },
        });
      } else {
        // В обычном режиме эмитим событие для родительского компонента
        emit('material-clicked', props.material);
      }
    };
    
    return {
      handleMaterialClick,
      isReallyPreviewMode
    };
  }
};
</script>

<style scoped>
.thumb-container {
  position: relative;
}

.thumb-container .duration {
  position: absolute;
  bottom: 10px;
  left: 15px;
  background: var(--yt-bg-primary);
  font-size: 0.8em;
  border-radius: 6px;
  padding: 2px 6px;
  color: var(--yt-text-secondary);
}

.img-thumbnail {
  border: 0;
  padding: 0;
}

.card-body {
  padding: 0;
}

.card-body .img-cover {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.bottom-part {
  padding: 0 1rem 1rem 1rem;
}

.card-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.card-title a {
  text-decoration: none;
  color: inherit;
}

.card-title a:hover {
  color: var(--bs-primary);
}

.placeholder-image {
  height: 120px;
  background-color: var(--yt-bg-secondary);
  border: 1px solid var(--yt-border-color);
}

/* Улучшение читаемости тегов */
.tag-badge {
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 0, 0, 0.1);
}
</style>